import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import type { NurseInShift } from "../utils/models";
import { shiftTypeToIndex } from "../utils/utilFunctions";
import { useHandleNurseChange } from "./useHandleNurseChange";

/**
 * Saves a scheduled shift's note: the write, the local `hospitalData` update and the success snackbar.
 * Shared by the daily-view inline note field and the mobile staff-row sheet, which each decide WHEN to
 * save. `saveNotes` resolves `true` when it wrote, `false` when the note was unchanged (nothing to
 * write), and rejects when the write fails — the mutation's own `onError` has already shown the error.
 */
export const useUpdateShiftNotes = (nurseInShift: NurseInShift) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { getWeekIndexByDate, getDayIndexByDateTime, updateNurseNotes } = useHospitalData();
	const {
		updateNurse,
		isLoading: { updateNurse: isSaving },
	} = useHandleNurseChange();

	const saveNotes = useCallback(
		async (notes: string): Promise<boolean> => {
			// DEFAULT: carried over from NurseInShiftNotesInput — a shift with no note and an empty field are
			// the same "no note", so clearing an absent note is not a change worth a write.
			if (notes === (nurseInShift.notes ?? "")) return false;

			const weekIndex = getWeekIndexByDate(nurseInShift.startTime);
			const dayNo = getDayIndexByDateTime(nurseInShift.startTime);
			const shiftIndex = shiftTypeToIndex(nurseInShift.shiftType);
			await updateNurse({
				nurse: { ...nurseInShift, notes },
				shiftIndex,
				dayNo,
				weekIndex,
			});
			updateNurseNotes(nurseInShift.id, weekIndex, shiftIndex, dayNo, notes, false);
			showSnackbar(t("notes.updated.success", { nurseName: nurseInShift.name }), "success");
			return true;
		},
		[nurseInShift, getWeekIndexByDate, getDayIndexByDateTime, updateNurse, updateNurseNotes, showSnackbar, t],
	);

	return { saveNotes, isSaving };
};
